import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { FAQ } from "@/components/FAQ";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { FeatureVisual, type FeatureKey } from "@/components/FeatureVisual";
import { whatsappUrl } from "@/lib/site";
import { CRM_VIDEO_TESTIMONIALS, CRM_WRITTEN_TESTIMONIALS } from "@/lib/crm-testimonials";
import crmHero from "@/assets/crm-hero.jpg";
import { StatusMonitor } from "@/components/StatusMonitor";

/* ---------- Modern CTA buttons (scoped to this page) ---------- */
const btnBase =
  "group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-6 py-3.5 text-sm font-semibold tracking-tight transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-ring)] min-h-[48px]";

function Sheen() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
    />
  );
}

function ArrowIcon({ className = "" }: { className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14" />
      <path d="m13 5 7 7-7 7" />
    </svg>
  );
}

function WhatsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.52 3.48A11.78 11.78 0 0 0 12.04 0C5.5 0 .19 5.3.19 11.84c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.83 11.83 0 0 0 5.64 1.43h.01c6.53 0 11.84-5.3 11.84-11.84 0-3.16-1.23-6.13-3.37-8.43Zm-3.06 11.86c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
    </svg>
  );
}

function CtaWhats({ href, children, className = "" }: { href: string; children: React.ReactNode; className?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      className={`${btnBase} text-white shadow-[0_12px_40px_-12px_rgba(37,211,102,0.7)] hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(37,211,102,0.85)] ${className}`}
      style={{ background: "linear-gradient(135deg,#22c55e 0%,#16a34a 55%,#15803d 100%)" }}
    >
      <Sheen />
      <span className="relative flex items-center gap-2">
        <WhatsIcon />
        {children}
        <ArrowIcon className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </a>
  );
}

function CtaPlans({ children = "Consultar planos", className = "" }: { children?: React.ReactNode; className?: string }) {
  return (
    <a
      href={whatsappUrl("Olá, me interessei no CRM SVG e gostaria de saber o valor aproximado")}
      target="_blank"
      rel="noopener"
      className={`${btnBase} text-white shadow-[0_14px_44px_-14px_oklch(0.62_0.22_258/0.8)] hover:-translate-y-0.5 hover:shadow-[0_20px_55px_-14px_oklch(0.62_0.22_258/0.95)] ${className}`}
      style={{ background: "linear-gradient(135deg,oklch(0.66 0.22 258) 0%,oklch(0.55 0.22 268) 100%)" }}
    >
      <Sheen />
      <span aria-hidden className="absolute inset-0 rounded-full ring-1 ring-inset ring-ink/20" />
      <span className="relative flex items-center gap-2">
        <WhatsIcon />
        {children}
        <ArrowIcon className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </a>
  );
}

function CtaGhostLink({ to, children, className = "" }: { to: string; children: React.ReactNode; className?: string }) {
  return (
    <Link
      to={to}
      className={`${btnBase} border border-ink/15 bg-ink/[0.04] text-foreground backdrop-blur-sm hover:border-[oklch(0.62_0.22_258/0.55)] hover:bg-ink/[0.08] hover:-translate-y-0.5 ${className}`}
    >
      <Sheen />
      <span className="relative flex items-center gap-2">
        {children}
        <ArrowIcon className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

export const Route = createFileRoute("/crm-yt")({
  head: () => ({
    meta: [
      { title: "CRM SVG | CRM com IA para WhatsApp que vende sozinho" },
      { name: "description", content: "CRM SVG: inteligência artificial conectada ao WhatsApp para atender, qualificar, agendar e vender 24/7. Funil automático, follow-ups, disparos em massa e dashboard completo." },
      { property: "og:title", content: "CRM SVG | IA que vende pelo WhatsApp 24/7" },
      { property: "og:description", content: "Atendimento, follow-up, agendamento e funil automatizados por IA direto no WhatsApp." },
      { property: "og:image", content: crmHero },
      { property: "og:url", content: "/crm-yt" },
    ],
    links: [{ rel: "canonical", href: "https://www.svgmultimidia.com.br/crm-yt" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "CRM SVG",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          description: "CRM com inteligência artificial integrado ao WhatsApp para vendas, atendimento e funil automatizados.",
          provider: { "@type": "Organization", name: "SVG Multimídia" },
        }),
      },
    ],
  }),
  component: CrmPage,
});

type FeatureCat = "IA" | "Funil" | "Atendimento" | "Integrações" | "Gestão";
const catTone: Record<FeatureCat, string> = {
  IA: "from-[oklch(0.62_0.22_280/0.25)] to-[oklch(0.55_0.20_258/0.15)] text-[oklch(0.85_0.12_280)] ring-[oklch(0.62_0.22_280/0.35)]",
  Funil: "from-[oklch(0.62_0.22_258/0.25)] to-[oklch(0.55_0.18_220/0.15)] text-[oklch(0.85_0.12_258)] ring-[oklch(0.62_0.22_258/0.35)]",
  Atendimento: "from-[oklch(0.62_0.18_150/0.25)] to-[oklch(0.55_0.18_180/0.15)] text-[oklch(0.88_0.14_150)] ring-[oklch(0.62_0.18_150/0.35)]",
  Integrações: "from-[oklch(0.65_0.18_50/0.25)] to-[oklch(0.55_0.20_25/0.15)] text-[oklch(0.85_0.14_60)] ring-[oklch(0.65_0.18_50/0.35)]",
  Gestão: "from-[oklch(0.60_0.20_200/0.25)] to-[oklch(0.55_0.18_258/0.15)] text-[oklch(0.85_0.12_210)] ring-[oklch(0.60_0.20_200/0.35)]",
};
const features: { k: FeatureKey; t: string; d: string; c: FeatureCat }[] = [
  { k: "ai-wa", c: "IA", t: "IA conectada ao WhatsApp", d: "Respostas automáticas com inteligência artificial direto no número da sua empresa, 24 horas por dia." },
  { k: "funnel", c: "Funil", t: "Funil organizado por IA", d: "A IA classifica cada conversa e move o lead pela etapa certa do CRM, sem operação manual." },
  { k: "followup", c: "Funil", t: "Follow-ups automáticos", d: "Reativação inteligente de leads frios com sequências programadas e personalizadas." },
  { k: "schedule", c: "Atendimento", t: "Agendamento pelo WhatsApp", d: "O cliente agenda sozinho na conversa. Sem fricção, sem perder oportunidade." },
  { k: "reminder", c: "Atendimento", t: "Lembretes automáticos", d: "Confirmações e lembretes enviados antes do horário marcado para reduzir faltas." },
  { k: "metrics", c: "Gestão", t: "Métricas completas", d: "Tempo de resposta, taxa de conversão, vendas, atendimentos — tudo em um painel claro." },
  { k: "analysis", c: "IA", t: "Análise de atendimento por IA", d: "A IA avalia conversas, identifica pontos de melhoria e sugere ajustes no script." },
  { k: "broadcast", c: "Atendimento", t: "Disparos em massa", d: "Envios via API oficial e não oficial, com segmentação por etiquetas e funil." },
  { k: "api", c: "Integrações", t: "API oficial do WhatsApp", d: "Conexão com a Cloud API oficial Meta para escala e segurança." },
  { k: "multi", c: "Integrações", t: "Múltiplos números", d: "Vários WhatsApps em uma única conta (taxa extra por número adicional)." },
  { k: "meta", c: "Integrações", t: "Integração com Meta Ads", d: "Vinculação direta com sua conta de anúncios Meta para rastrear leads de campanhas." },
  { k: "tags", c: "Funil", t: "Etiquetas automáticas", d: "Classificação automática de leads por interesse, etapa e comportamento." },
  { k: "notify", c: "Atendimento", t: "Notificações externas", d: "Avisos via WhatsApp para sua equipe sobre novos leads, agendamentos e alertas." },
  { k: "dashboard", c: "Gestão", t: "Dashboard completo", d: "Visão consolidada de atendimentos, resultados, equipe e desempenho por canal." },
  { k: "chats", c: "Atendimento", t: "Aba de conversas", d: "Responda leads direto pelo sistema, com histórico, perfil e contexto completo." },
  { k: "lead", c: "Gestão", t: "Perfil do lead", d: "Todas as informações e o histórico das negociações em um único lugar." },
  { k: "agents", c: "IA", t: "Agentes de IA dedicados", d: "Crie agentes de IA com personalidade e script da sua empresa direto no WhatsApp." },
  { k: "audio", c: "IA", t: "Áudios: envia e entende", d: "A IA escuta áudios do cliente e responde por áudio quando faz sentido." },
  { k: "media", c: "Atendimento", t: "Mídias, fotos, vídeos e links", d: "Envio completo de materiais para enriquecer o atendimento e fechar mais vendas." },
];

const steps = [
  { n: "01", t: "Conexão", d: "Conectamos seu WhatsApp ao CRM SVG, via API oficial ou número existente." },
  { n: "02", t: "Treinamento da IA", d: "Configuramos os agentes com o tom, ofertas e processo da sua empresa." },
  { n: "03", t: "Funil automático", d: "Definimos etapas, gatilhos, etiquetas e follow-ups." },
  { n: "04", t: "Operação 24/7", d: "A IA atende, qualifica, agenda e vende enquanto sua equipe foca em fechar." },
  { n: "05", t: "Evolução contínua", d: "Análises da própria IA orientam ajustes para vender cada vez mais." },
];

const faq = [
  { q: "O CRM SVG usa a API oficial do WhatsApp?", a: "Sim. Suportamos conexão via API oficial Meta Cloud e também conexões via API não oficial para casos específicos. Indicamos a melhor opção conforme seu volume e necessidade." },
  { q: "A IA realmente vende sozinha?", a: "A IA conduz o atendimento, qualifica o lead, envia materiais, agenda e em muitos casos fecha vendas simples. Para tickets mais altos, ela entrega o lead pronto para o time humano." },
  { q: "Posso conectar mais de um número de WhatsApp?", a: "Sim. O sistema suporta múltiplos números na mesma conta. Há uma taxa extra por número adicional conectado." },
  { q: "A IA entende áudios dos clientes?", a: "Sim. A IA transcreve e interpreta áudios recebidos e pode responder em texto ou áudio, conforme configurado." },
  { q: "Funciona com anúncios do Meta (Facebook e Instagram)?", a: "Sim. O CRM se integra à sua conta de anúncios Meta para rastrear leads vindos de campanhas e atribuir resultados." },
  { q: "Quanto tempo leva para implantar?", a: "A implantação técnica é rápida. O treinamento da IA e configuração do funil depende do seu processo — normalmente entre poucos dias e duas semanas." },
];

const videoTestimonials = CRM_VIDEO_TESTIMONIALS;
const writtenTestimonials = CRM_WRITTEN_TESTIMONIALS;


function CrmPage() {
  return (
    <>
      {/* HERO */}
      <section className="hero-bg relative overflow-hidden">
        <div className="container-svg pt-12 pb-16 md:pt-20 md:pb-24">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "CRM SVG" }]} />
          <div className="mt-6 grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <span className="eyebrow">CRM SVG · IA + WhatsApp</span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                <span className="text-gradient">O CRM com IA que vende sozinho</span> pelo WhatsApp da sua empresa
              </h1>
              <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
                Atendimento, qualificação, agendamento, follow-up e fechamento — tudo automatizado por agentes de inteligência artificial conectados direto ao seu WhatsApp, 24 horas por dia.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <CtaWhats href={whatsappUrl("Ola quero saber mais sobre o CRM SVG")}>Quero uma demonstração</CtaWhats>
                <CtaPlans />
                <CtaGhostLink to="/contato">Falar com a equipe</CtaGhostLink>
              </div>
              <dl className="mt-12 grid grid-cols-3 gap-6 border-t border-border pt-8">
                <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Operação</dt><dd className="mt-1 font-display text-lg font-semibold">24/7</dd></div>
                <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Canal</dt><dd className="mt-1 font-display text-lg font-semibold">WhatsApp</dd></div>
                <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Conexão</dt><dd className="mt-1 font-display text-lg font-semibold">API Oficial e Não Oficial</dd></div>
              </dl>
            </div>
            <Reveal>
              <div className="relative">
                <div className="absolute -inset-10 -z-10 rounded-[2.5rem] bg-[radial-gradient(60%_60%_at_50%_50%,oklch(0.62_0.22_258/0.35),transparent)]" />
                <Tilt3D max={8}>
                  <img
                    src={crmHero}
                    alt="Painel do CRM SVG com IA conectada ao WhatsApp"
                    width={1280}
                    height={960}
                    className="relative w-full rounded-3xl border border-ink/10 shadow-[0_30px_80px_-30px_oklch(0_0_0_/_0.8)]"
                  />
                </Tilt3D>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SOLUÇÃO */}
      <Section>
        <SectionHeader
          eyebrow="A Solução"
          title={<>Transforme seu WhatsApp em uma <span className="text-primary">máquina de atendimento e vendas</span> 24/7</>}
          description="Com o CRM SVG, cada mensagem recebida é respondida instantaneamente, qualificada, organizada no funil e acompanhada com follow-ups automáticos. Sua equipe foca no que realmente importa: fechar."
        />
        <div className="mt-10"><StatusMonitor /></div>
        <div className="mt-8 flex justify-center"><CtaPlans /></div>
      </Section>

      {/* FUNCIONALIDADES */}
      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader eyebrow="Funcionalidades" title={<>Tudo o que o <span className="text-primary">CRM SVG</span> faz pela sua empresa</>} />
        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <Reveal key={f.t} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <Tilt3D>
                <div className="card-glass group relative flex h-full flex-col overflow-hidden p-0 transition-all duration-500 hover:-translate-y-1.5 hover:border-primary/40 hover:shadow-[0_28px_60px_-28px_oklch(0.62_0.22_258/0.45)]">
                  {/* top hairline */}
                  <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                  {/* ambient hover glow */}
                  <div aria-hidden className="pointer-events-none absolute -inset-px rounded-[inherit] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{ background: "radial-gradient(60% 80% at 50% 0%, oklch(0.62 0.22 258 / 0.18), transparent 70%)" }} />
                  {/* visual */}
                  <div className="relative p-3">
                    <div className="relative overflow-hidden rounded-xl ring-1 ring-ink/10 shadow-[inset_0_1px_0_oklch(1_0_0/0.08),0_10px_30px_-15px_oklch(0_0_0/0.6)]">
                      <FeatureVisual k={f.k} />
                      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[color:var(--color-sidebar)] to-transparent" />
                    </div>
                    <span className={`absolute left-5 top-5 inline-flex items-center gap-1 rounded-full bg-gradient-to-br ${catTone[f.c]} px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 backdrop-blur`}>
                      <span className="size-1 rounded-full bg-current opacity-80" />
                      {f.c}
                    </span>
                  </div>
                  {/* content */}
                  <div className="relative flex flex-1 flex-col px-5 pb-5 pt-1">
                    <h3 className="text-[15px] font-semibold leading-snug tracking-tight text-foreground transition-colors duration-300 group-hover:text-primary">
                      {f.t}
                    </h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">{f.d}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-ink/[0.06] pt-3">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        {String(i + 1).padStart(2, "0")} / {String(features.length).padStart(2, "0")}
                      </span>
                      <span className="inline-flex size-7 items-center justify-center rounded-full bg-ink/[0.04] text-muted-foreground ring-1 ring-ink/10 transition-all duration-300 group-hover:bg-primary/15 group-hover:text-primary group-hover:ring-primary/40 group-hover:translate-x-0.5">
                        <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                      </span>
                    </div>
                  </div>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 flex justify-center"><CtaPlans /></div>
      </Section>

      {/* PROCESSO */}
      <Section>
        <SectionHeader eyebrow="Como funciona" title="Da conexão à operação 24/7" />
        <ol className="mt-12 grid gap-4 md:grid-cols-5">
          {steps.map((s, i) => (
            <Reveal key={s.n} as="li" delay={((i % 5) + 1) as 1 | 2 | 3 | 4 | 5}>
              <Tilt3D max={6}>
                <div className="card-glass h-full">
                  <span className="font-display text-2xl font-bold text-primary">{s.n}</span>
                  <h3 className="mt-3 font-semibold">{s.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </ol>
        <div className="mt-10 flex justify-center"><CtaPlans /></div>
      </Section>

      {/* DESTAQUE IA */}
      <Section className="bg-[color:var(--color-sidebar)]">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div>
              <span className="eyebrow">Agentes de IA</span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">Uma IA que conversa como gente — e fecha como vendedor</h2>
              <p className="mt-4 text-muted-foreground">
                Os agentes de IA do CRM SVG são treinados com a linguagem, ofertas e objeções da sua empresa. Entendem áudio, enviam mídias, fazem follow-up no tempo certo e mantêm o histórico completo de cada lead.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                {[
                  "Recebe e envia áudios automaticamente",
                  "Envia fotos, vídeos, PDFs e links",
                  "Reage a etiquetas e movimenta o funil",
                  "Aciona um humano quando faz sentido",
                ].map((i) => (
                  <li key={i} className="flex items-start gap-3"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary" />{i}</li>
                ))}
              </ul>
              <div className="mt-8"><CtaPlans /></div>
            </div>
          </Reveal>
          <Reveal delay={2}>
            <Tilt3D>
              <div className="card-glass">
                <div className="space-y-3 text-sm">
                  <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-primary/15 p-3"><strong className="block text-xs text-primary">Cliente</strong>Vocês atendem em Chapecó? Quanto custa?</div>
                  <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-ink/[0.05] p-3"><strong className="block text-xs text-muted-foreground">IA · CRM SVG</strong>Atendemos sim! Posso te enviar um resumo dos planos e já agendar uma conversa rápida hoje às 16h?</div>
                  <div className="ml-auto max-w-[60%] rounded-2xl rounded-tr-sm bg-primary/15 p-3"><strong className="block text-xs text-primary">Cliente</strong>🎙️ Áudio (0:12)</div>
                  <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-ink/[0.05] p-3"><strong className="block text-xs text-muted-foreground">IA · CRM SVG</strong>Perfeito! Agendado às 16h. Acabei de te enviar o link e um lembrete vai chegar 1h antes ✅</div>
                </div>
              </div>
            </Tilt3D>
          </Reveal>
        </div>
      </Section>

      {/* DEMONSTRAÇÃO EM VÍDEO */}
      <Section>
        <SectionHeader
          eyebrow="Demonstração"
          title={<>Veja o <span className="text-primary">CRM SVG</span> em ação</>}
          description="Assista à demonstração completa do CRM SVG com IA conectada ao WhatsApp."
        />
        <Reveal>
          <div className="mx-auto mt-10 max-w-4xl">
            <div className="relative aspect-video overflow-hidden rounded-3xl border border-ink/10 shadow-[0_30px_80px_-30px_oklch(0_0_0_/_0.8)]">
              <iframe
                src="https://www.youtube.com/embed/hekqf2hE_mI"
                title="Demonstração do CRM SVG"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                className="absolute inset-0 h-full w-full"
              />
            </div>
          </div>
        </Reveal>
        <div className="mt-10 flex justify-center"><CtaPlans /></div>
      </Section>

      <CTASection
        title="Quer ver o CRM SVG atendendo no seu WhatsApp?"
        text="Agende uma demonstração e veja a IA respondendo, qualificando, agendando e vendendo no seu próprio fluxo."
        buttonLabel="Quero uma demonstração"
      />

      {/* DEPOIMENTOS */}
      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader
          eyebrow="Depoimentos"
          title={<>Clientes vendendo mais com o <span className="text-primary">CRM SVG</span></>}
          description="Quem ativou a IA no WhatsApp ganhou tempo, produtividade e faturamento. Veja relatos reais."
        />

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {videoTestimonials.map((v, i) => (
            <Reveal key={v.id} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <Tilt3D max={6}>
                <div className="card-glass overflow-hidden p-2">
                  <div className="relative aspect-[9/16] overflow-hidden rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)]">
                    <iframe
                      src={`https://www.youtube.com/embed/${v.id}`}
                      title={v.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                      className="absolute inset-0 h-full w-full"
                    />
                  </div>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {writtenTestimonials.map((t, i) => (
            <Reveal key={t.name} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <Tilt3D max={5}>
                <figure className="card-glass flex h-full flex-col">
                  <div className="flex gap-0.5" aria-label="5 de 5 estrelas">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <svg key={k} width="14" height="14" viewBox="0 0 24 24" fill="#FBBC04" stroke="#FBBC04" strokeWidth="1.5" aria-hidden>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed">“{t.content}”</blockquote>
                  <figcaption className="mt-4 border-t border-border pt-3">
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </figcaption>
                </figure>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CONSULTAR VALORES */}
      <Section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_50%_0%,oklch(0.62_0.22_258/0.18),transparent_70%)]" />
        <div className="mx-auto max-w-2xl text-center">
          <span className="eyebrow">Planos e assinatura</span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">
            Fale com a gente e receba uma proposta sob medida para o seu <span className="text-primary">CRM SVG</span>
          </h2>
          <p className="mt-4 text-muted-foreground">
            Cada operação tem volume, integrações e necessidades diferentes. Envie uma mensagem no WhatsApp e consulte valores e condições personalizadas para o seu negócio.
          </p>
          <div className="mt-8 flex justify-center">
            <CtaPlans />
          </div>
        </div>
      </Section>

      <Section>
        <FAQ items={faq} />
      </Section>
    </>
  );
}